import { QDataTable, QButton, QCard, QAlert, QTypography } from '@kw/quokka-ui';
import { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

import { useLocalStorage } from '../../utils/useLocalStorage';
import { httpRequest } from '../../utils/network.service';
import { formatDateTime } from './utils';
import { AppSearch } from './AppSearch';
import { fetchService } from '../../utils/fetchService';

export const AppResubmission = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [accessToken = ''] = useLocalStorage('accessToken', '');

  const [searchResults, setSearchResults] = useState([]);
  const [resubmitResult, setResubmitResult] = useState('');
  const [severity, setSeverity] = useState<'error' | 'success'>('success');

  const resubmitApp = async row => {
    const { data, error } = await httpRequest({
      path: `${process.env.DEVICE_SERVICE_URL}/application/resubmit`,
      method: 'POST',
      body: { appId: row.applicationId, os: row.os, version: row.version },
    });

    if (error) {
      setResubmitResult(`Error resubmitting app`);
      setSeverity('error');
      return;
    }
    if (data) {
      if (['FAILED_TO_SUBMIT', 'FAILED'].includes(data.analysisStatus)) {
        setResubmitResult(`App resubmission failed to submit. ${data.analysisMessage || ''}`);
        setSeverity('error');
        return;
      }
      if (['PROCESSING'].includes(data.analysisStatus)) {
        setResubmitResult(`App is being processed now with version ${data.version}`);
        setSeverity('success');
        return;
      }
      if (['COMPLETE'].includes(data.analysisStatus)) {
        setResubmitResult(`App's status at MAST is complete. App data will be available soon on the Application list.`);
        setSeverity('success');
        return;
      }
      setResubmitResult(`App's status is ${data.analysisStatus}. ${data.analysisMessage || ''}`);
      setSeverity('error');
    }
  };

  const columns = useMemo(
    () => [
      { field: 'version', headerName: 'Version' },
      { field: 'analysisStatus', headerName: 'Status' },
      {
        field: 'updatedDate',
        headerName: 'Updated At',
        renderCell: row => (row.updatedDate ? formatDateTime(row.updatedDate) : ''),
      },
      {
        field: 'submit',
        headerName: 'Submit',
        renderCell: row =>
          ['FAILED', 'FAILED_TO_SUBMIT'].includes(row.analysisStatus) ? (
            <QButton
              variant="outlined"
              onClick={() => {
                resubmitApp(row);
              }}
            >
              submit app
            </QButton>
          ) : null,
      },
    ],
    [],
  );

  async function searchApps(appId: string, os: string): Promise<void> {
    try {
      const response = await fetchService(`/application/os/${os}/app/${appId}/status`, accessToken);

      if (response) {
        setSearchResults(response);
        setResubmitResult('');
      }
    } catch (error) {
      enqueueSnackbar(`Error searching for applications. error: ${error}`, { variant: 'error' });
      setSearchResults([]);
    }
  }
  return (
    <>
      <QTypography variant="h6Medium" sx={{ marginBottom: 2 }}>
        You can search for an application by its ID and platform, and resubmit it for analysis if its status is FAILED. The
        submitted app version might not match the version requested.
      </QTypography>
      {resubmitResult && (
        <QCard variant="outlined" sx={{ padding: 2, marginBottom: 2 }}>
          <QAlert severity={severity} sx={{ marginBottom: 2 }}>
            {resubmitResult}
          </QAlert>
        </QCard>
      )}
      <AppSearch searchFunc={(appId, os) => searchApps(appId, os)} />

      <QDataTable emptyTableText="No Apps Found" columns={columns} data={searchResults} searchConfig={['analysisStatus', 'version']} />
    </>
  );
};
